/* Shared inline PDF viewer used by the product PDF sections.
 *
 * Renders each page of a PDF to a canvas (no iframe, so no browser PDF chrome
 * and no nested scrollbars), samples the document for a dominant colour to
 * drive a glow, and offers a fullscreen pinch/tap zoom viewer.
 *
 * Sections enqueue config via window.PiusPdfQueue.push({...}); this file is
 * loaded with `defer`, so the queue is drained once on execution and further
 * pushes are handled immediately.
 */
(function () {
  'use strict';

  var LIB = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  var WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

  var libQueue = null;

  function loadLib(cb) {
    if (window.pdfjsLib) return cb();
    if (libQueue) return libQueue.push(cb);
    libQueue = [cb];
    var s = document.createElement('script');
    s.src = LIB;
    s.onload = function () {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER;
      var q = libQueue;
      libQueue = null;
      q.forEach(function (f) { f(); });
    };
    s.onerror = function () { libQueue = null; };
    document.head.appendChild(s);
  }

  /* Pick the colour the document actually reads as.
   *
   * A plain average is dominated by black body text and washes out to gray, so
   * pixels are weighted by chroma: a small saturated logo outweighs a page full
   * of neutral text. Falls back to a flat average when the page is essentially
   * monochrome. */
  function dominantColor(ctx, w, h) {
    var data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      return '200,170,130';
    }

    var cr = 0, cg = 0, cb = 0, cw = 0;
    var fr = 0, fg = 0, fb = 0, fn = 0;

    for (var i = 0; i < data.length; i += 40) {
      var r = data[i], g = data[i + 1], b = data[i + 2];
      var lum = 0.299 * r + 0.587 * g + 0.114 * b;
      if (lum > 240 || lum < 12) continue;

      fr += r; fg += g; fb += b; fn++;

      var mx = Math.max(r, g, b);
      var mn = Math.min(r, g, b);
      var chroma = mx - mn;
      if (chroma < 28) continue;

      cr += r * chroma; cg += g * chroma; cb += b * chroma; cw += chroma;
    }

    var r0, g0, b0;
    if (cw > 0) {
      r0 = cr / cw; g0 = cg / cw; b0 = cb / cw;
    } else if (fn > 0) {
      r0 = fr / fn; g0 = fg / fn; b0 = fb / fn;
    } else {
      return '200,170,130';
    }

    /* Nudge away from gray and up in brightness so the halo is visible
       against a light page background. */
    var avg = (r0 + g0 + b0) / 3;
    function boost(v) {
      var out = avg + (v - avg) * 1.7;
      out = out * 0.75 + 255 * 0.25;
      return Math.max(0, Math.min(255, Math.round(out)));
    }
    return boost(r0) + ',' + boost(g0) + ',' + boost(b0);
  }

  /* Render every page into `container` at `cssWidth`, cropping `cropTop` /
   * `cropBottom` percent off each page. Cropping is done by shrinking the
   * canvas and translating the render, so the trimmed area costs nothing. */
  function renderPages(pdf, container, cssWidth, cls, cropTop, cropBottom) {
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var chain = Promise.resolve();

    for (var i = 1; i <= pdf.numPages; i++) {
      (function (num) {
        chain = chain.then(function () {
          return pdf.getPage(num).then(function (page) {
            var base = page.getViewport({ scale: 1 });
            var vp = page.getViewport({ scale: (cssWidth / base.width) * dpr });

            var topPx = Math.round(vp.height * (cropTop / 100));
            var botPx = Math.round(vp.height * (cropBottom / 100));
            var visible = Math.max(16, vp.height - topPx - botPx);

            var canvas = document.createElement('canvas');
            canvas.className = cls;
            canvas.width = Math.round(vp.width);
            canvas.height = Math.round(visible);

            var ctx = canvas.getContext('2d');
            /* White backing: PDFs have no opaque background of their own. */
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            return page
              .render({
                canvasContext: ctx,
                viewport: vp,
                transform: [1, 0, 0, 1, 0, -topPx]
              })
              .promise.then(function () {
                container.appendChild(canvas);
                requestAnimationFrame(function () { canvas.classList.add('is-in'); });
                return canvas;
              });
          });
        });
      })(i);
    }
    return chain;
  }

  function init(opts) {
    var id = opts.uid;
    var glow = document.getElementById('pdf-glow-' + id);
    var wrap = document.getElementById('pdf-pages-' + id);
    var expandBtn = document.getElementById('pdf-expand-' + id);
    var lb = document.getElementById('pdf-lb-' + id);
    var lbPages = document.getElementById('pdf-lbpages-' + id);
    /* glow is optional — sections can switch it off */
    if (!wrap) return;

    var cropTop = Number(opts.cropTop) || 0;
    var cropBottom = Number(opts.cropBottom) || 0;
    /* Never let the two crops swallow the whole page. */
    if (cropTop + cropBottom > 90) {
      var scale = 90 / (cropTop + cropBottom);
      cropTop *= scale;
      cropBottom *= scale;
    }

    loadLib(function () {
      if (!window.pdfjsLib) return;
      var fitW = wrap.clientWidth || 900;

      window.pdfjsLib
        .getDocument({ url: opts.url, withCredentials: false })
        .promise.then(function (pdf) {
          return renderPages(pdf, wrap, fitW, 'pdf-vs__page', cropTop, cropBottom).then(
            function () {
              var first = glow && wrap.querySelector('canvas');
              if (first) {
                var rgb = dominantColor(first.getContext('2d'), first.width, first.height);
                glow.style.boxShadow =
                  '0 0 110px 45px rgba(' + rgb + ',0.32), 0 0 35px 8px rgba(' + rgb + ',0.20)';
              }
              return pdf;
            }
          );
        })
        .then(function (pdf) {
          if (!expandBtn || !lb || !lbPages) return;
          var lbScroll = lb.querySelector('.pdf-lb__scroll');
          if (!lbScroll) return;

          expandBtn.hidden = false;

          var built = false;
          var zoom = 1;
          var isTouch = window.matchMedia('(pointer: coarse)').matches;

          /* Zoom by widening the page stack and letting the container scroll.
             The anchor maths keeps whatever the reader was looking at fixed
             under the pinch midpoint instead of jumping to the corner. */
          function setZoom(z, ax, ay) {
            z = Math.max(1, Math.min(6, z));
            if (Math.abs(z - zoom) < 0.001) return;
            ax = ax == null ? lbScroll.clientWidth / 2 : ax;
            ay = ay == null ? lbScroll.clientHeight / 2 : ay;
            var rx = (lbScroll.scrollLeft + ax) / zoom;
            var ry = (lbScroll.scrollTop + ay) / zoom;
            zoom = z;
            lbPages.style.width = z * 100 + '%';
            lbScroll.scrollLeft = rx * z - ax;
            lbScroll.scrollTop = ry * z - ay;
          }

          function open() {
            lb.hidden = false;
            document.body.style.overflow = 'hidden';
            if (!built) {
              built = true;
              /* Render large so it stays sharp when zoomed. */
              renderPages(pdf, lbPages, 1400, 'pdf-lb__page', cropTop, cropBottom);
            }
            zoom = 1;
            lbPages.style.width = '100%';
            /* A full page scaled to a phone screen is unreadable, so open
               already zoomed in, horizontally centred on the text. */
            if (isTouch) {
              requestAnimationFrame(function () {
                setZoom(2.6);
                lbScroll.scrollLeft = (lbScroll.scrollWidth - lbScroll.clientWidth) / 2;
                lbScroll.scrollTop = 0;
              });
            }
          }

          function close() {
            lb.hidden = true;
            document.body.style.overflow = '';
          }

          expandBtn.addEventListener('click', open);

          lb.addEventListener('click', function (e) {
            var hit = e.target.closest ? e.target.closest('[data-act]') : null;
            var act = hit && hit.getAttribute('data-act');
            if (act === 'close' || e.target === lb) return close();
            if (act === 'in') return setZoom(zoom + 0.5);
            if (act === 'out') return setZoom(zoom - 0.5);
          });

          document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && !lb.hidden) close();
          });

          /* Pinch to zoom */
          var pinch = null;
          function spread(t) {
            var dx = t[0].clientX - t[1].clientX;
            var dy = t[0].clientY - t[1].clientY;
            return Math.sqrt(dx * dx + dy * dy) || 1;
          }

          lbScroll.addEventListener(
            'touchstart',
            function (e) {
              if (e.touches.length !== 2) return;
              var r = lbScroll.getBoundingClientRect();
              pinch = {
                d: spread(e.touches),
                z: zoom,
                ax: (e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left,
                ay: (e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top
              };
            },
            { passive: true }
          );

          lbScroll.addEventListener(
            'touchmove',
            function (e) {
              if (!pinch || e.touches.length !== 2) return;
              e.preventDefault();
              setZoom(pinch.z * (spread(e.touches) / pinch.d), pinch.ax, pinch.ay);
            },
            { passive: false }
          );

          lbScroll.addEventListener(
            'touchend',
            function (e) {
              if (e.touches.length < 2) pinch = null;
            },
            { passive: true }
          );

          /* Double tap / double click toggles between fit and readable */
          var lastTap = 0;
          lbScroll.addEventListener('click', function (e) {
            if (e.target.closest && e.target.closest('[data-act]')) return;
            var now = Date.now();
            if (now - lastTap < 300) {
              var r = lbScroll.getBoundingClientRect();
              setZoom(zoom > 1.6 ? 1 : 2.8, e.clientX - r.left, e.clientY - r.top);
            }
            lastTap = now;
          });
        })
        .catch(function (e) {
          console.warn('[pdf-viewer]', e);
        });
    });
  }

  var pending = window.PiusPdfQueue || [];
  window.PiusPdfQueue = { push: init };
  pending.forEach(init);
})();
